import {
  CalendarSlash,
  FileX,
  Headset,
  PhoneDisconnect,
  PhoneSlash,
  PhoneTransfer,
  Pill,
  Radio,
  Robot,
  ShareNetwork,
  Siren,
  Storefront,
  Tooth,
  Voicemail,
} from '@phosphor-icons/react'
import {
  Archive,
  Bed,
  Building,
  Calendar,
  CalendarCheck,
  CalendarMinus,
  CalendarPlus,
  CalendarRotate,
  CalendarXmark,
  Car,
  CardShield,
  ChatLines,
  ChatMinusIn,
  Clock,
  CreditCard,
  DollarCircle,
  Eye,
  Folder,
  Globe,
  InfoCircle,
  Language,
  MailOut,
  MapPinXmark,
  MapXmark,
  MicrophoneMute,
  MinusCircle,
  PageEdit,
  PhoneDisabled,
  PhoneIncome,
  PhoneMinus,
  PhoneOutcome,
  PhonePlus,
  PhoneXmark,
  Printer,
  Repeat,
  Send,
  Shield,
  ShieldAlert,
  ShieldPlusIn,
  ShieldXmark,
  SparksSolid,
  Tv,
  UserBadgeCheck,
  UserPlus,
  UserStar,
  UserXmark,
  VideoCamera,
  XmarkCircle,
} from 'iconoir-react'
import type { ComponentType } from 'react'

// Icons come from iconoir-react and @phosphor-icons/react; chips only ever pass
// className, so the map is typed to that shared surface.
type TagIcon = ComponentType<{ className?: string }>

/**
 * Icons for the built-in (system) tags from lib/getBuiltInTags.ts.
 * Semantic clusters share an icon family (calendar, phone, shield, user...)
 * so related tags read as related. Icons render in the chip's ink color.
 */
const builtInTagIcons: Record<string, TagIcon> = {
  // Appointments
  'Appointment Scheduled': CalendarPlus,
  'Appointment Rescheduled': CalendarRotate,
  'Appointment Confirmation': CalendarCheck,
  'Appointment Not Scheduled': CalendarMinus,
  'Canceled Appointment': CalendarXmark,
  'Broken Appointment': CalendarSlash,
  'No Appointment': Calendar,
  'Schedule Availability': Clock,
  Unscheduled: CalendarMinus,

  // Calls
  'Missed Call': PhoneXmark,
  Callback: PhoneOutcome,
  'New Patient Call': PhonePlus,
  Connected: PhoneIncome,
  'Not Connected': PhoneDisabled,
  'Unable To Contact': PhoneDisabled,
  'Abandoned Call': PhoneDisconnect,
  Unanswered: PhoneMinus,
  'Unscheduled Call': PhoneMinus,
  'Wrong Number': PhoneSlash,
  'Office Transfer': PhoneTransfer,
  Voicemail: Voicemail,
  'Answering Machine': Robot,
  'Answering Service': Headset,
  'No Recording': MicrophoneMute,
  'Unscheduled Chat': ChatMinusIn,

  // Patients & leads
  'New Patient': UserPlus,
  'Current Patient': UserBadgeCheck,
  'No Patient Found': UserXmark,
  'No Patient Linked': UserXmark,
  'Potential Lead': UserStar,
  'False Lead': XmarkCircle,
  'Scheduled With Competitor': Storefront,
  'Closed Lost: Other': MinusCircle,
  'Service Not Offered': Tooth,

  // Insurance & billing
  'Insurance: State / HMO': Shield,
  'Insurance: Out of Network': ShieldXmark,
  'Insurance Issue': ShieldAlert,
  'Insurance Referral': ShieldPlusIn,
  Billing: CreditCard,
  'Billing/Insurance': CardShield,
  'Pricing Too High': DollarCircle,

  // Referrals & requests
  'Patient Referral': Send,
  'Office Referral': Send,
  'Referral Request': Send,
  'Interoffice Request': MailOut,
  'Request for Records': Folder,
  'Prescription Question': Pill,

  // Channels & sources
  'New Chat': ChatLines,
  'New Form': PageEdit,
  'Unscheduled Imported Form': PageEdit,
  'New Online Scheduler': Globe,
  'New Virtual Consultation': VideoCamera,
  'Social Media': ShareNetwork,
  TV: Tv,
  Radio: Radio,
  Print: Printer,
  'Drive-by': Car,
  'Drive-By': Car,

  // Geography & misc
  'Office Too Far': MapPinXmark,
  'Out of Range': MapXmark,
  'Other Language': Language,
  'Orthodontic Office': Building,
  Sedation: Bed,
  Repeat: Repeat,
  'Needs Review': Eye,
  'Invalid Note': FileX,
  Other: InfoCircle,
  Archived: Archive,

  // Specials (chips also give these distinct fills / recipes)
  Emergency: Siren,
  AI: SparksSolid,
}

/** Icon for a built-in tag name, or undefined for user-created tags. */
export function getBuiltInTagIcon(name: string): TagIcon | undefined {
  if (name.toUpperCase() === `AI`) return SparksSolid
  return builtInTagIcons[name]
}
